// ─────────────────────────────────────────────
//  Facility rules engine + facility lifecycle status (Phase 5.4).
//
//  Per-facility behavior rules live in `facilities/{id}.rules` and are
//  resolved through DEFAULT_FACILITY_RULES at runtime — the app reads these
//  instead of hard-coding (ROADMAP 5.4 SC4). Pure module: no Firebase.
//
//  Facility lifecycle: Active / Standby / Archived (`facilityStatus`), with
//  the legacy `isActive` boolean kept in sync for back-compat (every pre-5.4
//  consumer keys off isActive).
// ─────────────────────────────────────────────

export const DEFAULT_FACILITY_RULES = {
  // A supervising doctor covering more than this many rooms is overloaded
  // (D-08 flag). Clinical range 1–4.
  supervisionRatio: 4,
  // Longest approvable break window, minutes.
  breakMaxMinutes: 60,
  // How many of a doctor's rooms may run approved break windows at once.
  breakSimultaneousPerDoctor: 1,
  // Room-condition posts also ping the day's AIC (not just the room doctor).
  notifyAicOnCondition: false,
  // Cross-facility floats allowed from this facility's day board.
  floatAllowed: true,
};

// Merge a facility doc's stored rules over the defaults, clamping the
// numeric knobs to sane clinical ranges so a bad write can't break the board.
export function resolveFacilityRules(facility) {
  const stored = facility?.rules && typeof facility.rules === "object" ? facility.rules : {};
  const merged = { ...DEFAULT_FACILITY_RULES, ...stored };
  const num = (v, fallback) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : fallback);
  return {
    supervisionRatio: Math.min(4, Math.max(1, num(merged.supervisionRatio, 4))),
    breakMaxMinutes: Math.min(240, Math.max(10, num(merged.breakMaxMinutes, 60))),
    breakSimultaneousPerDoctor: Math.min(10, Math.max(1, num(merged.breakSimultaneousPerDoctor, 1))),
    notifyAicOnCondition: merged.notifyAicOnCondition === true,
    floatAllowed: merged.floatAllowed !== false,
  };
}

// ── Lifecycle status ──────────────────────────
// The three lifecycle states a facility doc may declare. This is also the
// ALLOW-LIST: a `facilityStatus` string outside this set is not a status we
// understand, and is deliberately NOT read as active (see below).
export const FACILITY_STATUSES = ["active", "standby", "archived"];

// THE SINGLE DEFINITION OF A FACILITY'S LIFECYCLE STATE. Everything that asks
// "is this facility running?" resolves through here — domain/facilities
// `isFacilityActive` is literally `resolveFacilityStatus(f) === "active"` — so
// the boolean gate and the label an administrator reads cannot drift apart.
//
// ⚠️ THEY DID DRIFT, AND THE DRIFT FAILED OPEN. This function used to end with
//       return facility?.isActive === false ? "standby" : "active";
// which makes "active" the answer to everything it does not recognise, while
// `isFacilityActive` independently demanded positive proof of activity. Two
// consequences, both wrong in the dangerous direction:
//
//   1. `{ facilityStatus: "inactive" }` — the obvious word an operator reaches
//      for when switching a campus off — is not in FACILITY_STATUSES, so it
//      fell through to the legacy branch, found `isActive !== false`, and
//      resolved to "active". Deliberately deactivating a facility ACTIVATED it.
//   2. A bare `{}` resolved to "active" here and to NOT-active in
//      `isFacilityActive`. `{}` is reachable: `updateFacility` is
//      setDoc(merge:true), so Setup ▸ Google Sheet Import can bring a facility
//      doc into existence carrying neither `facilityStatus` nor `isActive`.
//
// "Active" is not a label, it is an ENROLMENT. It puts a facility into the
// nightly scheduled import (functions/dailyImport.js reads the `facilities`
// collection live), the cross-facility All-AICs channel, and MD-call
// cross-board sync — and that import FULL-RESETS a campus: assignments deleted,
// rooms cleared, roster replaced. Guessing "active" for a value we cannot read
// hands a real hospital's board to a job that rewrites it. So the unknown cases
// FAIL CLOSED to "standby": the non-destructive, reversible, operator-visible
// state (Setup ▸ Facility Status renders it as a chip, so the refusal is
// something a human can see and correct). Never "archived" — we are declining
// to guess, not asserting the campus is retired.
export function resolveFacilityStatus(facility) {
  const s = facility?.facilityStatus;
  if (FACILITY_STATUSES.includes(s)) return s;
  // ⚠️ ABSENT IS NOT THE SAME AS UNRECOGNISED, and that distinction is the
  // whole back-compat story. null/undefined mean the doc simply predates
  // `facilityStatus` (createFacility still writes only `isActive: true`), so
  // the legacy boolean is the best evidence available and is trusted. Any
  // OTHER value — "inactive", "", 0, a typo — is a positive statement we could
  // not parse, and no amount of legacy `isActive` may rescue it.
  if (s != null) return "standby";
  // Legacy docs: only the isActive boolean exists. It must be exactly `true`;
  // missing, `undefined`, `"true"` and `1` all mean "we don't know" → standby.
  return facility?.isActive === true ? "active" : "standby";
}

export const FACILITY_STATUS_META = {
  active:   { label: "Active",   tint: "#e8edf9", text: "#1e40af" },
  standby:  { label: "Standby",  tint: "#fef8ec", text: "#7a4f00" },
  archived: { label: "Archived", tint: "#f0f2f5", text: "#565d82" },
};

// ── Facility profile options (SC1) ────────────
export const SPECIALTY_ROOM_OPTIONS = ["Endo", "OB", "Cath Lab", "Radiology"];

// ── Rule consumers' helpers ───────────────────
// Approve-modal minute options capped by the facility's breakMaxMinutes.
const BASE_MINUTE_OPTIONS = [10, 15, 20, 30, 45, 60, 90, 120];
export function breakMinuteOptions(maxMinutes) {
  const cap = Number(maxMinutes) > 0 ? Number(maxMinutes) : 60;
  const opts = BASE_MINUTE_OPTIONS.filter((m) => m <= cap);
  return opts.length ? opts : [cap];
}

// Rooms currently running an approved break window that are supervised by
// this doctor (assignment attending wins over the live room doc, matching
// RoomCard's precedence). Drives the breakSimultaneousPerDoctor guard.
export function runningBreaksForDoctor(rooms = [], assignments = [], doctorId) {
  if (!doctorId) return [];
  return rooms.filter((r) => {
    if (!r?.breakStartedAt) return false;
    const asg = assignments.find((a) => a.orRoom === r.name);
    const roomDoctor = asg?.attendingId || r.supervisingDoctorId || null;
    return roomDoctor === doctorId;
  });
}
